import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { useUserSystem } from '@/components/ConfigProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function LoginPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const { reloadSystem } = useUserSystem();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/projects';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already signed in, redirect
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate(redirectTo, { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate, redirectTo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/local/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success && data.data.session_id) {
        // Store session ID
        localStorage.setItem('session_id', data.data.session_id);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        
        // Reload user system to update auth state
        await reloadSystem();
        
        // Navigate to intended page
        navigate(redirectTo, { replace: true });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="space-y-6 p-8 pb-16 md:pb-8 h-full overflow-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (isSignedIn) {
    return null; // Will redirect via useEffect
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left side - Form */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        padding: '3rem 1.5rem'
      }} className="bg-background dark:bg-background">
        <div style={{ margin: '0 auto', width: '100%', maxWidth: '28rem' }}>
          {/* Logo and title */}
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '3.5rem',
              height: '3.5rem',
              borderRadius: '0.75rem',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }} className="bg-muted text-foreground">
              🔐
            </div>
            <h2 style={{
              marginTop: '2rem',
              fontSize: '1.875rem',
              fontWeight: 'bold'
            }} className="text-foreground">
              Login at here ...
            </h2>
            <p style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem'
            }} className="text-muted-foreground">
              to access your projects, tasks, and settings
            </p>
          </div>

          {/* Form */}
          <div style={{ marginTop: '2.5rem' }}>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Username field */}
              <div>
                <Label 
                  htmlFor="username" 
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem'
                  }}
                >
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="username"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Password field */}
              <div>
                <Label 
                  htmlFor="password" 
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem'
                  }}
                >
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Submit button */}
              <div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  {isLoading ? (
                    <>
                      <div style={{
                        marginRight: '0.5rem',
                        width: '1.25rem',
                        height: '1.25rem',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} className="border-2 border-primary-foreground/30 border-t-primary-foreground" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Right side - Branding */}
      <div style={{
        position: 'relative',
        flex: 1,
        display: window.innerWidth >= 1024 ? 'block' : 'none'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0
        }} className="bg-muted">
          {/* Decorative pattern */}
          <div style={{ 
            position: 'absolute', 
            inset: 0,
            opacity: 0.1
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '24rem',
              height: '24rem',
              borderRadius: '50%',
              filter: 'blur(100px)',
              transform: 'translate(-50%, -50%)'
            }} className="bg-foreground" />
            <div style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '24rem',
              height: '24rem',
              borderRadius: '50%',
              filter: 'blur(100px)',
              transform: 'translate(50%, 50%)'
            }} className="bg-foreground" />
          </div>
          
          {/* Content */}
          <div style={{
            position: 'relative',
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem'
          }}>
            <div style={{ maxWidth: '32rem' }}>
              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '10rem',
                  height: '10rem',
                  marginBottom: '1.5rem',
                  fontSize: '2.5rem'
                }} className="bg-background/20 backdrop-blur-sm">
                  <img src="/logo-and-footer.png" alt="Paracel Image" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </div>
              </div>
              <h2 style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }} className="text-foreground">
                Welcome to Paracel PcodeX platform
              </h2>
              <p style={{
                fontSize: '1.25rem',
                lineHeight: '1.75'
              }} className="text-muted-foreground">
                Powerful automation and intelligent workflows for your development projects. Sign in to get started.
              </p>
              <div style={{
                marginTop: '3rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                <p style={{
                  fontSize: '0.875rem'
                }} className="text-muted-foreground">
                  Developed by MINHTT
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
